/* ─────────────────────────────────────────────────────────────────────────
   VES cart behaviours, loaded GLOBALLY from layout/theme.liquid so every
   page (collection, product, pack-card grids, etc.) has the same wiring:

     • Click-capture interceptor for [name="add"] buttons:
         - already-in-cart   → open drawer, never duplicate-add
         - pack-with-bundle  → open upsell modal (if present on page)
         - otherwise         → pass through to Horizon's product-form

     • Cart cache + qty-1 normalizer (every variant ever in cart capped to 1).
     • Upgrade-to-bundle handler   ([data-ves-upgrade-bundle])
     • Fast pack-add optimistic UI ([data-ves-pack-add])
     • Cart-line remove handler    ([data-ves-remove])

   Previously this code lived inside sections/ves-upsell-modal.liquid's
   {% javascript %} block. Section-scoped JS only loads when the host
   section is on the page, which meant collection-page cards (no
   upsell-modal section) lost the upgrade-bundle / pack-add handlers and
   buttons did nothing. Moved here so it's available everywhere.
   ───────────────────────────────────────────────────────────────────────── */

  (function () {
    if (window.__vesUpsellInit) return;
    window.__vesUpsellInit = true;

    function init() {
      var modal = document.querySelector('[data-ves-upsell]');
      var currentVariant = modal ? modal.dataset.currentVariant : null;

      function open() {
        if (!modal) return;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        var closeBtn = modal.querySelector('[data-upsell-close]');
        if (closeBtn) closeBtn.focus();
      }

      function close() {
        if (!modal) return;
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }

      // ── Cart cache + qty-1 normalizer ─────────────────────────────────────
      // Every VES product is a digital one-of-a-kind asset, so a variant
      // must never appear twice in the cart AND no line may have quantity
      // greater than 1. Two enforcement layers:
      //
      //  1. Click interceptor: if the variant is already in cart, swallow
      //     the click and just open the drawer instead of adding.
      //
      //  2. Cart normalizer: after every cart:update event AND on init,
      //     fetch /cart.js and immediately force any line with qty > 1
      //     down to qty 1 via /cart/update.js. Catches duplicates that
      //     slipped through the interceptor (rapid double-clicks, direct
      //     /cart/add hits from third-party scripts, race conditions).
      var cartVariantIds = new Set();
      var normalizingCart = false;

      function refreshCartCache() {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : { items: [] }; })
          .then(function (c) {
            cartVariantIds = new Set((c.items || []).map(function (i) { return String(i.variant_id); }));
            return c;
          })
          .catch(function () { return { items: [] }; });
      }

      function getCartSectionIds() {
        var ids = [];
        document.querySelectorAll('cart-items-component').forEach(function (c) {
          var sid = c.dataset.sectionId;
          if (sid && ids.indexOf(sid) === -1) ids.push(sid);
        });
        return ids;
      }

      // Force qty to 1 on every line that has > 1. Re-dispatches cart:update
      // with the refreshed section markup so the drawer rehydrates.
      function normalizeQuantities(cart) {
        if (!cart || !cart.items) return;
        var overFlows = cart.items.filter(function (i) { return i.quantity > 1; });
        if (overFlows.length === 0) return;
        if (normalizingCart) return; // prevent recursion
        normalizingCart = true;

        var updates = {};
        overFlows.forEach(function (i) { updates[i.variant_id] = 1; });
        var sectionIds = getCartSectionIds();

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: sectionIds.join(',')
          })
        })
          .then(function (r) { return r.json(); })
          .then(function (response) {
            normalizingCart = false;
            cartVariantIds = new Set((response.items || []).map(function (i) { return String(i.variant_id); }));
            // Rebroadcast so the drawer / cart icon refresh.
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: response,
                sourceId: 'ves-cart-normalizer',
                data: {
                  source: 'product-form-component',
                  sections: response.sections || {}
                }
              }
            }));
          })
          .catch(function () { normalizingCart = false; });
      }

      function refreshAndNormalize() {
        return refreshCartCache().then(normalizeQuantities);
      }

      function openCartDrawer() {
        var drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
      }

      // Mobile scroll fix. On mobile, Horizon's cart-drawer pushes a history
      // entry on open (so the phone's back button closes the drawer) and calls
      // history.back() on close. The browser then auto-restores the previous
      // entry's scroll position, which was recorded as 0, because the dialog
      // scroll-lock had the body position:fixed when the entry was created.
      // Net effect: closing the cart jumped the page to the top. While the
      // drawer is open we switch history.scrollRestoration to 'manual' so the
      // browser stays out of it; dialog.js already restores the real position
      // itself (scrollTo(#previousScrollY)). Restored to 'auto' shortly after
      // close, once the history.back() popstate has settled.
      (function () {
        if (!('scrollRestoration' in window.history)) return;
        var drawerEl = document.querySelector('cart-drawer-component');
        if (!drawerEl) return;
        drawerEl.addEventListener('dialog:open', function () {
          window.history.scrollRestoration = 'manual';
        });
        drawerEl.addEventListener('dialog:close', function () {
          window.setTimeout(function () {
            window.history.scrollRestoration = 'auto';
          }, 400);
        });
      })();

      // Loading state for the drawer while an add is in flight. The store's
      // /cart/add.js (+ the section morph) takes ~1s, during which the
      // optimistic line has no real controls yet. This makes that interval
      // read as "adding…" instead of "broken": a top progress bar + the
      // optimistic line dimmed + interaction suppressed until the morph lands.
      function setCartLoading(on) {
        var cart = document.querySelector('.ves-cart');
        if (cart) cart.classList.toggle('is-adding', !!on);
      }

      // Most cart:update events already carry the full cart JSON in
      // detail.resource (/cart/update.js and /cart/change.js responses), so
      // the variant cache can be rebuilt straight from the event with no
      // follow-up /cart.js round-trip. Returns false for non-cart-shaped
      // resources (e.g. /cart/add.js returns only the added line).
      function absorbCartResource(c) {
        if (!c || !c.items || typeof c.item_count !== 'number') return false;
        cartVariantIds = new Set(c.items.map(function (i) { return String(i.variant_id); }));
        return true;
      }

      refreshAndNormalize();
      document.addEventListener('cart:update', function (e) {
        var sourceId = e.detail && e.detail.sourceId;
        // The authoritative cart markup has landed (the morph runs off this
        // same event), so clear any loading state.
        setCartLoading(false);
        var resource = e.detail && e.detail.resource;
        // Self-dispatched normalizer event: already normalized; just sync the
        // cache (from the event payload when possible).
        if (sourceId === 'ves-cart-normalizer') {
          if (!absorbCartResource(resource)) refreshCartCache();
          return;
        }
        // Single clean pack-add: cache was updated inline and qty is always 1,
        // so no normalize; refresh in the background (/cart/add.js's response
        // is the added line, not the cart, so absorb can't help here).
        if (sourceId === 'ves-pack-add') {
          refreshCartCache();
          return;
        }
        // Everything else (removals, Horizon quantity updates, bundle
        // upgrade): the event resource is normally the full cart, so absorb
        // it and normalize from it with zero extra requests; fall back to a
        // fetch only when the payload isn't cart-shaped.
        if (absorbCartResource(resource)) {
          normalizeQuantities(resource);
        } else {
          refreshAndNormalize();
        }
      });

      // ─────────────────────────────────────────────────────────────────────
      // Unified click-capture interceptor.
      //
      // Runs in CAPTURE phase on `document`, before Horizon's
      // <add-to-cart-component> `on:click` handler and before the form's
      // submit event ever fires. This is the single point where we decide
      // whether an add-to-cart click should:
      //   a) Be blocked because the variant is already in cart (open the
      //      drawer instead).
      //   b) Be redirected into the upsell modal (Type-A pack-with-bundle
      //      flow, never adds, never opens the drawer).
      //   c) Pass through to Horizon's normal product-form-component XHR.
      //
      // Buttons inside the modal and forms tagged data-no-upsell ALWAYS
      // pass through (they're the modal-resolution / opt-out paths).
      // ─────────────────────────────────────────────────────────────────────
      document.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('button[type="submit"][name="add"]') : null;
        if (!btn) return;
        var form = btn.closest('form');
        if (!form) return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;
        var variantId = String(idInput.value);

        // (a) Already in cart → open drawer, never add twice.
        //     `cartVariantIds` is built from /cart.js only, no optimistic
        //     writes, so a previously-failed add never leaves stale state
        //     in the Set that would block the next click. Rapid double-
        //     clicks are caught by the qty-1 normalizer instead.
        if (cartVariantIds.has(variantId)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          openCartDrawer();
          return;
        }

        // (b) Modal trigger. Only fires for the current page's pack-with-
        //     bundle PDP and only for buttons OUTSIDE the modal that don't
        //     carry data-no-upsell. Cancel the click so neither Horizon's
        //     XHR nor the form submission ever happens.
        var insideModal = !!btn.closest('[data-ves-upsell]');
        var optedOut = form.hasAttribute('data-no-upsell');
        if (modal && !insideModal && !optedOut && variantId === currentVariant) {
          e.preventDefault();
          e.stopImmediatePropagation();
          open();
          return;
        }

        // (c) Pass through. Horizon's <add-to-cart-component> on:click and
        //     <product-form-component> on:submit handle the actual XHR.
      }, true);

      // ── Modal close handlers ──────────────────────────────────────────────
      if (modal) {
        modal.addEventListener('click', function (e) {
          if (e.target.matches && e.target.matches('[data-upsell-close]')) close();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
        });

        // Close the modal whenever a cart:update event fires from inside it
        // AND the add succeeded (didError !== true). Horizon dispatches
        // CartAddEvent with `data.didError = true` on failure (422, 429,
        // etc.), and we DON'T want to close in that case, the user should
        // see the error and be able to retry.
        document.addEventListener('cart:update', function (e) {
          if (!modal.classList.contains('is-open')) return;
          var target = e.target;
          if (!(target && target.closest && target.closest('[data-ves-upsell]'))) return;
          if (e.detail && e.detail.data && e.detail.data.didError) return;
          close();
        });
      }

      // ── Fast pack-add handler ─────────────────────────────────────────────
      // Custom handler for the modal's "Continue with the pack" button (and
      // any future [data-ves-pack-add]). Skips Horizon's product-form-
      // component pipeline (which does an unnecessary /cart.js follow-up
      // round-trip plus quantity-selector hydration we don't need) and runs
      // optimistic UI so the modal-to-drawer transition starts immediately,
      // before the network request returns.
      document.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-ves-pack-add]') : null;
        if (!btn) return;
        if (btn.disabled || btn.dataset.addInFlight === '1') return;
        e.preventDefault();
        addPackFast(btn);
      });

      // ── Upsell attribution ────────────────────────────────────────────────
      // Stamp the cart so the resulting order records whether the bundle-upsell
      // modal was accepted or declined, and for which source pack. Shopify Flow
      // can then tag orders from these attributes, and admin reports break the
      // data down by tag. Accept is folded into the upgrade's existing
      // /cart/update.js call (no extra request); decline fires a small
      // fire-and-forget update (the pack-add path uses /cart/add.js, which does
      // not carry cart attributes). `_ves_upsell_surface` is "modal" only when
      // the action came from inside the modal, so other upgrade surfaces (hero,
      // sticky cart, pack cards) don't pollute the modal's accept rate.
      function upsellSource(btn) {
        try {
          var host = btn && btn.closest ? btn.closest('[data-ves-upsell]') : null;
          if (host && host.dataset && host.dataset.upsellSource) return host.dataset.upsellSource;
          var m = location.pathname.match(/\/products\/([^/?#]+)/);
          return m ? m[1] : '';
        } catch (e) { return ''; }
      }
      function upsellAttributes(outcome, btn) {
        return {
          '_ves_upsell': outcome,
          '_ves_upsell_surface': (btn && btn.closest && btn.closest('[data-ves-upsell]')) ? 'modal' : 'other',
          '_ves_upsell_source': upsellSource(btn)
        };
      }
      function stampUpsell(outcome, btn) {
        try {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: upsellAttributes(outcome, btn) })
          }).catch(function () {});
        } catch (e) {}
      }

      function addPackFast(btn) {
        var variantId = btn.dataset.variantId;
        var productId = btn.dataset.productId || '';
        if (!variantId) return;
        // Only the modal's "Continue with the pack" is a true upsell decline;
        // the same handler also runs for ordinary pack-card / pricing adds.
        var declinedFromModal = !!(btn.closest && btn.closest('[data-ves-upsell]'));

        // Already in cart → just open the drawer, don't add a second line.
        // (Digital packs are single-purchase; qty is always 1.) Mirrors the
        // submit-button interceptor's behavior so pack cards and the PDP
        // form behave the same.
        if (cartVariantIds.has(variantId)) {
          openCartDrawer();
          return;
        }

        btn.dataset.addInFlight = '1';
        btn.disabled = true;

        var sectionIds = getCartSectionIds();

        // ── Optimistic step 1: insert a placeholder line into the drawer
        //    so the user sees the new pack the instant the modal starts
        //    closing. The morph from /cart/add.js will replace this with
        //    authoritative markup. If the request fails, we remove this
        //    node in the catch block.
        var cic = document.querySelector('cart-drawer-component cart-items-component');
        var optimisticNode = null;
        if (cic) {
          var holder = document.createElement('div');
          holder.innerHTML = buildOptimisticItemHtml(btn);
          optimisticNode = holder.firstElementChild;
          // If the drawer is currently rendering empty-state, swap in a fresh
          // items wrapper. Otherwise prepend into the existing items list.
          var itemsList = cic.querySelector('.ves-cart__items');
          if (itemsList) {
            itemsList.prepend(optimisticNode);
          } else {
            cic.querySelector('.ves-cart__empty')?.remove();
            var bodyWrap = document.createElement('div');
            bodyWrap.className = 'ves-cart__body';
            bodyWrap.innerHTML = '<div class="ves-cart__items"></div>';
            bodyWrap.firstChild.appendChild(optimisticNode);
            cic.appendChild(bodyWrap);
          }
        }

        // ── Optimistic step 2: close modal + open drawer + show loading.
        if (modal) close();
        openCartDrawer();
        setCartLoading(true);

        // ── Network step: single /cart/add.js with sections.
        var fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        if (sectionIds.length > 0) fd.append('sections', sectionIds.join(','));

        fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/add.js status ' + r.status);
            return r.json();
          })
          .then(function (response) {
            if (declinedFromModal) stampUpsell('declined', btn);
            // Keep the in-cart Set current without a follow-up /cart.js
            // (the cart:update listener skips refreshAndNormalize for us).
            cartVariantIds.add(String(variantId));
            // Dispatch cart:update with the section markup so cart-items-
            // component morphs the optimistic placeholder out and the real
            // line in. The listener clears the loading state off this event.
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: response,
                sourceId: 'ves-pack-add',
                data: {
                  source: 'product-form-component',
                  itemCount: 1,
                  productId: productId,
                  sections: response.sections || {}
                }
              }
            }));
          })
          .catch(function (err) {
            console.error('[ves-pack-add] failed:', err);
            // Roll back: remove optimistic placeholder, reopen modal so the
            // user can retry. The remaining cart state is whatever was
            // before the click.
            if (optimisticNode && optimisticNode.isConnected) optimisticNode.remove();
            if (modal) open();
          })
          .finally(function () {
            btn.disabled = false;
            delete btn.dataset.addInFlight;
            // Safety net: clear loading even if no cart:update fired (e.g. an
            // error path where the morph never happened).
            setCartLoading(false);
          });
      }

      // Builds an optimistic .ves-cart__item line from data-* attrs on the
      // clicked button. Details get corrected
      // when the section morph from /cart/add.js lands a fraction of a
      // second later.
      function buildOptimisticItemHtml(btn) {
        function esc(s) {
          return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        }
        var title = esc(btn.dataset.productTitle || '');
        var url = esc(btn.dataset.productUrl || '#');
        var img = esc(btn.dataset.productImg || '');
        var price = esc(btn.dataset.productPrice || '');
        var compare = esc(btn.dataset.productCompare || '');
        var compareHtml = '';
        if (compare && compare !== price && compare !== '$0' && compare !== '') {
          compareHtml = '<span class="ves-cart__compare">' + compare + '</span>';
        }
        var imgHtml = img ? '<img src="' + img + '" alt="' + title + '" loading="lazy">' : '';
        return [
          '<article class="ves-cart__item ves-cart__item--optimistic" data-key="ves-optimistic-' + esc(btn.dataset.variantId) + '">',
            '<a href="' + url + '" class="ves-cart__cover">' + imgHtml + '</a>',
            '<div class="ves-cart__item-body">',
              '<a href="' + url + '" class="ves-cart__name">' + title + '</a>',
              '<div class="ves-cart__price-row">',
                '<span class="ves-cart__price">' + price + '</span>',
                compareHtml,
              '</div>',
            '</div>',
          '</article>'
        ].join('');
      }

      // ── Upgrade-to-bundle handler ─────────────────────────────────────────
      // Any button with [data-ves-upgrade-bundle] swaps every included-pack
      // line in the cart for the bundle in a SINGLE network round-trip via
      // /cart/update.js (which accepts `updates: { variant_id: qty }` and
      // both removes lines set to 0 AND adds lines set to non-zero in the
      // same call).
      //
      // Optimistic UI runs in parallel: modal closes immediately, drawer
      // opens immediately, included-pack rows get visually removed, and the
      // bundle row gets injected, all before the XHR returns. The morph
      // from the response replaces those placeholders with authoritative
      // server markup. On failure we roll back.
      document.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-ves-upgrade-bundle]') : null;
        if (!btn) return;
        if (btn.disabled || btn.dataset.upgradeInFlight === '1') return;
        e.preventDefault();
        upgradeToBundle(btn);
      });

      function upgradeToBundle(btn) {
        var bundleVariant = btn.dataset.bundleVariantId;
        var bundleProductId = btn.dataset.bundleProductId || '';
        var includedRaw = btn.dataset.includedVariants || '';
        var included = includedRaw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (!bundleVariant) return;

        btn.dataset.upgradeInFlight = '1';
        btn.disabled = true;

        var sectionIds = getCartSectionIds();

        // ── Optimistic UI ───────────────────────────────────────────────
        // (1) Hide every cart row whose variant is in the included list
        //     OR whose key starts with one of those variant ids (the cart
        //     line key format is `<variantId>:<hash>`).
        var includedSet = {};
        included.forEach(function (v) { includedSet[v] = true; });
        var hiddenRows = [];
        document.querySelectorAll('cart-drawer-component [data-key]').forEach(function (row) {
          var key = row.getAttribute('data-key') || '';
          var vid = key.split(':')[0];
          if (includedSet[vid]) {
            row.dataset.vesPrevDisplay = row.style.display || '';
            row.style.display = 'none';
            hiddenRows.push(row);
          }
        });

        // (2) Insert an optimistic line for the bundle.
        var cic = document.querySelector('cart-drawer-component cart-items-component');
        var optimisticNode = null;
        if (cic) {
          var holder = document.createElement('div');
          holder.innerHTML = buildOptimisticBundleHtml(btn);
          optimisticNode = holder.firstElementChild;
          var itemsList = cic.querySelector('.ves-cart__items');
          if (itemsList) {
            itemsList.prepend(optimisticNode);
          } else {
            cic.querySelector('.ves-cart__empty')?.remove();
            var bodyWrap = document.createElement('div');
            bodyWrap.className = 'ves-cart__body';
            bodyWrap.innerHTML = '<div class="ves-cart__items"></div>';
            bodyWrap.firstChild.appendChild(optimisticNode);
            cic.appendChild(bodyWrap);
          }
        }

        // (3) Close modal + open drawer + show loading in parallel.
        if (modal) close();
        openCartDrawer();
        setCartLoading(true);

        // ── Single network round-trip ───────────────────────────────────
        // /cart/update.js with `updates` accepts both removals (qty 0) AND
        // adds (qty 1 for a variant not yet in cart). One POST handles
        // both. Pass `sections=` so the response carries the rehydrated
        // cart-items markup.
        var updates = {};
        included.forEach(function (v) { updates[v] = 0; });
        updates[bundleVariant] = 1;

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: updates,
            attributes: upsellAttributes('accepted', btn),
            sections: sectionIds.join(',')
          })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/update.js status ' + r.status);
            return r.json();
          })
          .then(function (response) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: response,
                sourceId: btn.id || 'ves-upsell-bundle-upgrade',
                data: {
                  source: 'product-form-component',
                  itemCount: 1,
                  productId: bundleProductId,
                  sections: response.sections || {}
                }
              }
            }));
          })
          .catch(function (err) {
            console.error('[ves-upsell] bundle upgrade failed:', err);
            // Roll back optimistic UI.
            if (optimisticNode && optimisticNode.isConnected) optimisticNode.remove();
            hiddenRows.forEach(function (row) {
              row.style.display = row.dataset.vesPrevDisplay || '';
              delete row.dataset.vesPrevDisplay;
            });
            if (modal) open();
          })
          .finally(function () {
            btn.disabled = false;
            delete btn.dataset.upgradeInFlight;
            setCartLoading(false);
          });
      }

      // Builds an optimistic .ves-cart__item line for the bundle from the
      // upgrade button's data-* attrs. Matches the structure of the
      // authoritative server markup so the morph swap is smooth.
      function buildOptimisticBundleHtml(btn) {
        function esc(s) {
          return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        }
        var title = esc(btn.dataset.bundleTitle || '');
        var url = esc(btn.dataset.bundleUrl || '#');
        var img = esc(btn.dataset.bundleImg || '');
        var price = esc(btn.dataset.bundlePrice || '');
        var compare = esc(btn.dataset.bundleCompare || '');
        var packCount = parseInt(btn.dataset.bundlePackCount || '0', 10);
        var compareHtml = '';
        if (compare && compare !== price && compare !== '$0' && compare !== '') {
          compareHtml = '<span class="ves-cart__compare">' + compare + '</span>';
        }
        var imgHtml = img ? '<img src="' + img + '" alt="' + title + '" loading="lazy">' : '';
        return [
          '<article class="ves-cart__item ves-cart__item--optimistic" data-key="ves-optimistic-' + esc(btn.dataset.bundleVariantId) + '">',
            '<a href="' + url + '" class="ves-cart__cover">' + imgHtml + '</a>',
            '<div class="ves-cart__item-body">',
              '<a href="' + url + '" class="ves-cart__name">' + title + '</a>',
              '<div class="ves-cart__price-row">',
                '<span class="ves-cart__price">' + price + '</span>',
                compareHtml,
              '</div>',
            '</div>',
          '</article>'
        ].join('');
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
